import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  EyeOff,
  FileText,
  Keyboard,
  Layers,
  MessageSquare,
  Pause,
  Play,
  Radio,
  Search,
  Settings,
  Swords,
  Volume2,
  VolumeX,
} from 'lucide-react';
import type {
  CardDefinition,
  ClientGameView,
  GameIntent,
  PublicCardView,
  TrackingProviderId,
} from '#shared';
import { soundEngine, type SoundEventId, type SoundPreferences } from '../audio/soundEngine.ts';
import { getSessionBaseUrl, playtableApi } from '../lib/clientApi.ts';
import { StreamCompanionAndObsSuite } from './CalendarAndStreamingModal.tsx';
import { RichChatMessageText } from './LobbyQueueReplayModals.tsx';

export type SidebarTabId = 'game' | 'logs' | 'cards' | 'deck' | 'chat' | 'tracking' | 'stream' | 'settings';

interface TableSidebarProps {
  view: ClientGameView;
  overlaySecret: string | null;
  onOverlaySecretChanged?: (next: string) => void;
  side: 'left' | 'right';
  onChangeSide: (side: 'left' | 'right') => void;
  viewMode: 'follow' | 'grid';
  onChangeViewMode: (mode: 'follow' | 'grid') => void;
  reducedMotion: boolean;
  onToggleReducedMotion: (next: boolean) => void;
  onDispatch: (intent: GameIntent) => void;
  onHostAction: (action: Record<string, unknown>) => void;
  onOpenDeckLoader: () => void;
  onOpenCosmetics?: () => void;
  onOpenCartOptimizer?: () => void;
  onOpenReportModal?: () => void;
  onOpenReplayModal?: () => void;
  onInspectCard: (card: PublicCardView) => void;
  onLeaveTable: () => void;
}

export function TableSidebar({
  view,
  overlaySecret,
  onOverlaySecretChanged,
  side,
  onChangeSide,
  viewMode,
  onChangeViewMode,
  reducedMotion,
  onToggleReducedMotion,
  onDispatch,
  onHostAction,
  onOpenDeckLoader,
  onOpenCosmetics,
  onOpenCartOptimizer,
  onOpenReportModal,
  onOpenReplayModal,
  onInspectCard,
  onLeaveTable,
}: TableSidebarProps) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTabId>('game');
  const [logCategory, setLogCategory] = useState<'all' | 'hp' | 'library' | 'battlefield' | 'other'>('all');
  const [cardQuery, setCardQuery] = useState('');
  const [catalogHits, setCatalogHits] = useState<CardDefinition[]>([]);
  const [savedForLater, setSavedForLater] = useState<CardDefinition[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatChannel, setChatChannel] = useState<'table' | 'spectator'>('table');
  const [whisperSeatId, setWhisperSeatId] = useState<string>('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [webhookUrls, setWebhookUrls] = useState<Record<TrackingProviderId, string>>({
    play_nice: '',
    mythic_track: '',
    topdeck: '',
  });
  const [webhookStatus, setWebhookStatus] = useState<string | null>(null);
  const [soundPrefs, setSoundPrefs] = useState<SoundPreferences>(() => soundEngine.getPrefs());
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);

  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;

  useEffect(() => {
    if (activeTab !== 'tracking') return;
    playtableApi
      .listTrackingWebhooks()
      .then((res) => {
        const next: Record<TrackingProviderId, string> = { play_nice: '', mythic_track: '', topdeck: '' };
        for (const h of res.webhooks) {
          next[h.provider] = h.webhookUrl;
        }
        setWebhookUrls(next);
      })
      .catch(() => {});
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'cards' || !cardQuery.trim()) {
      setCatalogHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await playtableApi.searchCards(cardQuery.trim(), false);
        if (!cancelled) setCatalogHits(res.cards.slice(0, 8));
      } catch {
        // ignore
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activeTab, cardQuery]);

  const navItems: { id: SidebarTabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'game', label: 'Game', icon: Swords },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'cards', label: 'Cards', icon: Search },
    { id: 'deck', label: 'Deck', icon: Layers },
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'tracking', label: 'Tracking', icon: BarChart3 },
    { id: 'stream', label: 'Stream', icon: Radio },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const handleSelectTab = (id: SidebarTabId) => {
    if (expanded && activeTab === id) {
      setExpanded(false);
    } else {
      setActiveTab(id);
      setExpanded(true);
    }
  };

  const copyText = (key: string, val: string) => {
    navigator.clipboard?.writeText(val);
    setCopiedLabel(key);
    setTimeout(() => setCopiedLabel(null), 1400);
  };

  // All public cards currently visible across battlefield/graveyard/command for quick sidebar lookup
  const publicCardsOnTable: PublicCardView[] = [];
  for (const s of Object.values(view.seats)) {
    for (const c of [...s.zones.battlefield, ...s.zones.command, ...s.zones.graveyard]) {
      if (c.card) publicCardsOnTable.push(c);
    }
  }
  const matchingCards = cardQuery.trim()
    ? publicCardsOnTable.filter((c) => c.card?.name.toLowerCase().includes(cardQuery.trim().toLowerCase()))
    : publicCardsOnTable.slice(0, 8);

  const filteredLogs = view.recentLog.filter((entry) => {
    if (logCategory === 'all') return true;
    if (logCategory === 'hp') return entry.type.includes('LIFE') || entry.type.includes('COUNTER') || entry.type.includes('DAMAGE');
    if (logCategory === 'library') return entry.type.includes('DRAWN') || entry.type.includes('SHUFFLE') || entry.type.includes('SCRY') || entry.type.includes('LOOK') || entry.type.includes('MILL');
    if (logCategory === 'battlefield') return entry.type.includes('CARD_') || entry.type.includes('TOKEN') || entry.type.includes('UNTAP');
    return true;
  });

  const privateHandUrl = `${getSessionBaseUrl()}?tableId=${encodeURIComponent(view.tableId)}&session=private`;
  const obsOverlayUrl = overlaySecret
    ? `${getSessionBaseUrl()}?tableId=${encodeURIComponent(view.tableId)}&overlaySecret=${encodeURIComponent(overlaySecret)}`
    : null;

  return (
    <aside
      className={`hidden md:flex ${
        side === 'left' ? 'flex-row-reverse border-r' : 'flex-row border-l'
      } border-slate-800/90 bg-[#070b14] shrink-0 h-full select-none`}
    >
      {/* Vertical Icon Rail */}
      <div className="w-14 py-2 flex flex-col items-center gap-1.5 border-slate-800/80 bg-[#050811]">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle sidebar panel"
          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80"
        >
          {expanded ? (
            side === 'right' ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />
          ) : side === 'right' ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {navItems.map((item) => {
          const Icon = item.icon;
          const active = expanded && activeTab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelectTab(item.id)}
              className={`w-11 py-2 rounded-xl flex flex-col items-center gap-1 text-[9px] font-semibold transition-colors ${
                active
                  ? 'bg-indigo-600/30 border border-indigo-400/60 text-indigo-100'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Expanded Drawer Content */}
      {expanded && (
        <div className="w-72 p-3 overflow-y-auto overscroll-contain space-y-3 text-xs text-slate-200 border-l border-slate-800/80">
          {activeTab === 'game' && (
            <div className="space-y-3">
              <div className="font-bold text-sm text-white flex items-center justify-between">
                <span>Setup · {view.inviteCode}</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-900 border border-slate-700 text-slate-400">
                  {view.spectatorCount} watching
                </span>
              </div>

              <button
                type="button"
                onClick={() => copyText('share', `${getSessionBaseUrl()}?invite=${view.inviteCode}`)}
                className="w-full py-2 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 font-semibold flex items-center justify-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>{copiedLabel === 'share' ? 'Copied Invite Link!' : `Share Game (${view.inviteCode})`}</span>
              </button>

              {/* Turn Order Table */}
              <div className="rounded-xl bg-slate-900/90 border border-slate-800 p-2.5 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex justify-between">
                  <span>Turn Order</span>
                  <span>Turn {view.turnNumber}</span>
                </div>
                {view.seatOrder.map((sid, idx) => {
                  const s = view.seats[sid];
                  if (!s) return null;
                  return (
                    <div key={sid} className="flex items-center justify-between py-1 text-[11px]">
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="text-slate-500 w-4">{idx + 1}</span>
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.profileColor }} />
                        <span className={s.status === 'eliminated' ? 'line-through text-slate-500' : 'font-semibold'}>
                          {s.displayName}
                        </span>
                      </div>
                      <span className="text-[10px] uppercase text-slate-400">
                        {view.activeSeatId === sid ? 'ACTIVE' : s.status}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => onDispatch({ type: 'START_MATCH', autoShuffle: true })}
                className="w-full py-2 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold flex items-center justify-center gap-1.5 shadow"
              >
                <Play className="w-3.5 h-3.5" />
                <span>Randomize & Start Match</span>
              </button>

              {view.status === 'active' && (
                <button
                  type="button"
                  onClick={() => onDispatch({ type: 'PAUSE_SAVE_MATCH' })}
                  className="w-full py-1.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-amber-500/40 text-amber-200 font-semibold flex items-center justify-center gap-1.5"
                >
                  <Pause className="w-3.5 h-3.5" />
                  <span>Pause & Save Pod</span>
                </button>
              )}

              {view.status === 'paused' && (
                <button
                  type="button"
                  onClick={() => onDispatch({ type: 'RESUME_SAVED_MATCH' })}
                  className="w-full py-1.5 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold flex items-center justify-center gap-1.5"
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>Resume Saved Pod</span>
                </button>
              )}

              {(view.isHost || view.isJudge) && view.lateJoinRequests.length > 0 && (
                <div className="p-2.5 rounded-xl bg-amber-950/60 border border-amber-500/50 space-y-1.5">
                  <div className="text-[10px] font-bold uppercase text-amber-300">
                    Late Join Requests ({view.lateJoinRequests.length})
                  </div>
                  {view.lateJoinRequests.map((req) => (
                    <div key={req.requestId} className="flex items-center justify-between gap-1 text-[11px]">
                      <span className="truncate font-semibold">{req.displayName}</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => playtableApi.resolveLateJoin(view.tableId, req.requestId, true)}
                          className="px-2 py-0.5 rounded bg-emerald-500 text-slate-950 font-bold text-[10px]"
                        >
                          Seat
                        </button>
                        <button
                          type="button"
                          onClick={() => playtableApi.resolveLateJoin(view.tableId, req.requestId, false)}
                          className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {onOpenReportModal && (
                <button
                  type="button"
                  onClick={onOpenReportModal}
                  className="w-full py-1.5 px-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/50 text-amber-200 font-bold"
                >
                  Report Match Outcome / Winner
                </button>
              )}

              {onOpenReplayModal && (
                <button
                  type="button"
                  onClick={onOpenReplayModal}
                  className="w-full py-1.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold"
                >
                  Open Scrubbable Replay
                </button>
              )}

              {(view.isHost || view.isJudge) && (
                <button
                  type="button"
                  onClick={() => onDispatch({ type: 'RESET_MATCH' })}
                  className="w-full py-1.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-amber-300 font-semibold"
                >
                  Reset Table State
                </button>
              )}

              <button
                type="button"
                onClick={onLeaveTable}
                className="w-full py-2 px-3 rounded-xl bg-rose-600/90 hover:bg-rose-500 text-white font-bold"
              >
                Leave Table
              </button>

              {/* Shared Table Notepad */}
              <div className="pt-2 border-t border-slate-800 space-y-1">
                <div className="text-[10px] font-bold uppercase text-slate-400">Shared Table Notepad</div>
                <textarea
                  rows={3}
                  defaultValue={view.sharedNotes}
                  onBlur={(e) => onDispatch({ type: 'UPDATE_SHARED_NOTES', notes: e.target.value })}
                  placeholder="Rule 0 notes, triggers, emblems..."
                  className="w-full p-2 rounded-lg bg-slate-900 border border-slate-800 text-[11px]"
                />
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-white">Game Logs</span>
                <button
                  type="button"
                  onClick={() => {
                    const text = filteredLogs.map((l) => `[T${l.turnNumber} #${l.seq}] ${l.summary}`).join('\n');
                    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `playtable-${view.inviteCode}-log.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700 text-[10px] flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  <span>Export</span>
                </button>
              </div>

              <div className="flex flex-wrap gap-1">
                {(
                  [
                    ['all', 'All'],
                    ['hp', 'HP'],
                    ['library', 'Library'],
                    ['battlefield', 'Battlefield'],
                  ] as const
                ).map(([cat, label]) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setLogCategory(cat)}
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                      logCategory === cat
                        ? 'bg-sky-500/20 border-sky-400 text-sky-200'
                        : 'bg-slate-900 border-slate-800 text-slate-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-1 max-h-[68vh] overflow-y-auto divide-y divide-slate-800/60">
                {filteredLogs
                  .slice()
                  .reverse()
                  .map((e) => (
                    <div key={e.seq} className="py-1.5 text-[11px] flex items-start justify-between gap-1.5">
                      <div>
                        <span className="font-mono text-amber-400 mr-1.5">T{e.turnNumber}</span>
                        <span className="text-slate-200">{e.summary}</span>
                      </div>
                      {(view.isHost || view.isJudge) && (
                        <button
                          type="button"
                          onClick={() => onDispatch({ type: 'ROLLBACK_TO_SEQ', targetSeq: e.seq })}
                          className="px-1.5 py-0.5 rounded bg-slate-900 hover:bg-amber-500 hover:text-slate-950 text-[9px] shrink-0"
                        >
                          Undo to
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {activeTab === 'cards' && (
            <div className="space-y-2.5">
              <div className="font-bold text-sm text-white">Card Search & Table Inspector</div>
              <input
                value={cardQuery}
                onChange={(e) => setCardQuery(e.target.value)}
                placeholder='Search visible or Scryfall cards (e.g. "Sol Ring")...'
                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700"
              />

              {matchingCards.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] font-bold uppercase text-slate-400">Visible on Table</div>
                  {matchingCards.map((c) => (
                    <button
                      key={c.instanceId}
                      type="button"
                      onClick={() => onInspectCard(c)}
                      className="w-full p-2 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-left flex justify-between items-center"
                    >
                      <span className="font-semibold text-slate-100 truncate">{c.card?.name}</span>
                      <span className="text-[10px] text-slate-400 uppercase">{c.zone}</span>
                    </button>
                  ))}
                </div>
              )}

              {catalogHits.length > 0 && (
                <div className="space-y-1 pt-1 border-t border-slate-800">
                  <div className="text-[10px] font-bold uppercase text-cyan-300">Scryfall Catalog</div>
                  {catalogHits.map((cd) => (
                    <div
                      key={cd.scryfallId}
                      className="p-2 rounded-lg bg-slate-900/90 border border-slate-800 flex items-center justify-between gap-1.5"
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-100 truncate">{cd.name}</div>
                        <div className="text-[10px] text-slate-400 truncate">{cd.typeLine}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {mySeat && (
                          <button
                            type="button"
                            onClick={() =>
                              onDispatch({
                                type: 'CREATE_TOKEN',
                                token: {
                                  name: cd.name,
                                  typeLine: cd.typeLine,
                                  power: cd.power,
                                  toughness: cd.toughness,
                                  colors: cd.colors,
                                  oracleText: cd.oracleText,
                                  imageUrl: cd.imageUrl,
                                },
                                count: 1,
                              })
                            }
                            className="px-1.5 py-0.5 rounded bg-emerald-600/30 border border-emerald-500/50 text-emerald-200 text-[10px] font-bold"
                          >
                            +Token
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setSavedForLater((prev) =>
                              prev.some((x) => x.scryfallId === cd.scryfallId) ? prev : [...prev, cd]
                            )
                          }
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[10px]"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {savedForLater.length > 0 && (
                <div className="space-y-1 pt-2 border-t border-slate-800">
                  <div className="text-[10px] font-bold uppercase text-amber-300 flex justify-between">
                    <span>Saved for Later ({savedForLater.length})</span>
                    <button
                      type="button"
                      onClick={() => setSavedForLater([])}
                      className="text-slate-500 hover:text-slate-300"
                    >
                      Clear
                    </button>
                  </div>
                  {savedForLater.map((cd) => (
                    <div
                      key={cd.scryfallId}
                      className="p-1.5 rounded bg-slate-900 border border-slate-800 flex items-center justify-between text-[11px]"
                    >
                      <span className="truncate">{cd.name}</span>
                      {mySeat && (
                        <button
                          type="button"
                          onClick={() =>
                            onDispatch({
                              type: 'CREATE_TOKEN',
                              token: {
                                name: cd.name,
                                typeLine: cd.typeLine,
                                power: cd.power,
                                toughness: cd.toughness,
                                colors: cd.colors,
                                oracleText: cd.oracleText,
                                imageUrl: cd.imageUrl,
                              },
                              count: 1,
                            })
                          }
                          className="text-emerald-300 font-bold text-[10px]"
                        >
                          Spawn
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'deck' && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={onOpenDeckLoader}
                className="w-full py-2 px-3 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-extrabold"
              >
                Deck Manager / Import
              </button>
              {onOpenCosmetics && (
                <button
                  type="button"
                  onClick={onOpenCosmetics}
                  className="w-full py-2 px-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/50 text-amber-200 font-bold"
                >
                  Playmat & Card-Art Sleeves
                </button>
              )}
              {onOpenCartOptimizer && (
                <button
                  type="button"
                  onClick={onOpenCartOptimizer}
                  className="w-full py-2 px-3 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/50 text-emerald-200 font-bold"
                >
                  Buy Deck / Cheapest Cart
                </button>
              )}
              {mySeat?.deckMeta ? (
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <div className="font-bold text-white">{mySeat.deckMeta.name}</div>
                  {mySeat.deckMeta.commanderNames.length > 0 && (
                    <div className="text-[11px] text-indigo-300 font-semibold">
                      Commander: {mySeat.deckMeta.commanderNames.join(' + ')}
                    </div>
                  )}
                  <div className="text-slate-400 text-[11px]">
                    {mySeat.deckMeta.mainCount} mainboard · {mySeat.deckMeta.sideboardCount} sideboard · Bracket B
                    {mySeat.deckMeta.detectedBracket}
                  </div>
                  <div className="text-[11px] text-slate-300">
                    Identity: {mySeat.deckMeta.colorIdentity.join('') || 'Colorless'}
                  </div>
                  <div className="text-amber-300 text-[11px]">
                    {mySeat.deckMeta.gameChangerCount} WotC Game Changers detected
                  </div>
                  {mySeat.availableTokens.length > 0 && (
                    <div className="pt-1.5 border-t border-slate-800 space-y-1">
                      <div className="text-[10px] font-bold uppercase text-emerald-400">
                        Auto-Detected Deck Tokens
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {mySeat.availableTokens.map((tok) => (
                          <button
                            key={tok.name}
                            type="button"
                            onClick={() => onDispatch({ type: 'CREATE_TOKEN', token: tok, count: 1 })}
                            className="px-2 py-0.5 rounded bg-emerald-950/70 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-[10px]"
                          >
                            + {tok.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-center">No deck loaded yet.</p>
              )}
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="flex flex-col h-[74vh] justify-between gap-2">
              <div className="flex gap-1">
                {(['table', 'spectator'] as const).map((ch) => (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => setChatChannel(ch)}
                    className={`flex-1 py-1 rounded-lg font-bold capitalize ${
                      chatChannel === ch ? 'bg-indigo-600/30 border border-indigo-400 text-white' : 'bg-slate-900 text-slate-400'
                    }`}
                  >
                    {ch}
                  </button>
                ))}
              </div>

              {mySeat && chatChannel === 'table' && (
                <select
                  value={whisperSeatId}
                  onChange={(e) => setWhisperSeatId(e.target.value)}
                  className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-800 text-[10px] text-slate-300"
                >
                  <option value="">Broadcast to Table (use [[Card Name]] to link cards)</option>
                  {view.seatOrder
                    .filter((sid) => sid !== mySeat.seatId && view.seats[sid]?.publicId)
                    .map((sid) => (
                      <option key={sid} value={sid}>
                        Whisper privately to {view.seats[sid].displayName}
                      </option>
                    ))}
                </select>
              )}

              <div className="flex-1 overflow-y-auto space-y-2 p-2 rounded-xl bg-slate-900/60 border border-slate-800">
                {view.chat.filter((m) => m.channel === chatChannel).length === 0 ? (
                  <div className="text-slate-500 text-center py-8">
                    No messages yet — say glhf or type [[Sol Ring]]!
                  </div>
                ) : (
                  view.chat
                    .filter((m) => m.channel === chatChannel)
                    .map((m) => {
                      const parent = m.replyToId ? view.chat.find((x) => x.id === m.replyToId) : null;
                      const isMine = mySeat?.publicId && m.senderPublicId === mySeat.publicId;
                      return (
                        <div key={m.id} className="p-1.5 rounded-lg bg-slate-950/70 border border-slate-800/80 space-y-1 text-[11px]">
                          {parent && (
                            <div className="text-[9px] text-slate-400 border-l-2 border-indigo-400 pl-1.5 truncate">
                              Replying to {parent.senderName}: {parent.text}
                            </div>
                          )}
                          <div className="flex items-start justify-between gap-1">
                            <div className="leading-snug">
                              <span className="font-bold text-amber-300">{m.senderName}: </span>
                              {editingMsgId === m.id ? (
                                <form
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    onDispatch({ type: 'EDIT_CHAT', messageId: m.id, newText: editingText });
                                    setEditingMsgId(null);
                                  }}
                                  className="inline-flex gap-1 mt-1"
                                >
                                  <input
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    className="px-1.5 py-0.5 rounded bg-slate-900 border border-slate-700 text-[10px]"
                                  />
                                  <button type="submit" className="px-1.5 rounded bg-emerald-600 text-white text-[9px]">
                                    Save
                                  </button>
                                </form>
                              ) : (
                                <span className={m.deleted ? 'italic text-slate-500' : 'text-slate-200'}>
                                  <RichChatMessageText
                                    text={m.text}
                                    onClickCardName={async (cardName) => {
                                      const res = await playtableApi.searchCards(cardName, false);
                                      const hit = res.cards[0];
                                      if (hit) {
                                        onInspectCard({
                                          instanceId: `chat_preview_${hit.scryfallId}`,
                                          ownerSeatId: view.viewerSeatId ?? 'seat_1',
                                          controllerSeatId: view.viewerSeatId ?? 'seat_1',
                                          zone: 'revealed',
                                          tapped: false,
                                          faceDown: false,
                                          flipped: false,
                                          counters: {},
                                          attachedToId: null,
                                          exiledByInstanceId: null,
                                          row: 'other',
                                          freePos: null,
                                          isToken: false,
                                          isCommander: false,
                                          enteredZoneSeq: 0,
                                          card: hit,
                                        });
                                      }
                                    }}
                                  />
                                  {m.editedAt && !m.deleted && (
                                    <span className="text-[9px] text-slate-500 ml-1">(edited)</span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {!m.deleted && (
                            <div className="flex flex-wrap items-center justify-between gap-1 pt-0.5">
                              <div className="flex items-center gap-1">
                                {['👍', '🔥', '💀', '😂'].map((em) => {
                                  const cnt = m.reactions?.[em]?.length ?? 0;
                                  return (
                                    <button
                                      key={em}
                                      type="button"
                                      onClick={() => onDispatch({ type: 'REACT_CHAT', messageId: m.id, emoji: em })}
                                      className={`px-1 py-0.2 rounded text-[9px] ${
                                        cnt > 0
                                          ? 'bg-indigo-600/30 border border-indigo-400/50 text-indigo-100'
                                          : 'bg-slate-900 text-slate-500 hover:text-slate-300'
                                      }`}
                                    >
                                      {em} {cnt > 0 ? cnt : ''}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex items-center gap-1.5 text-[9px] text-slate-400">
                                <button type="button" onClick={() => setReplyToId(m.id)} className="hover:text-white">
                                  Reply
                                </button>
                                {isMine && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingMsgId(m.id);
                                      setEditingText(m.text);
                                    }}
                                    className="hover:text-amber-300"
                                  >
                                    Edit
                                  </button>
                                )}
                                {(isMine || view.isHost || view.isJudge) && (
                                  <button
                                    type="button"
                                    onClick={() => onDispatch({ type: 'DELETE_CHAT', messageId: m.id })}
                                    className="hover:text-rose-400"
                                  >
                                    Del
                                  </button>
                                )}
                                {!isMine && m.senderPublicId && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => playtableApi.blockPlayer(m.senderPublicId)}
                                      className="hover:text-rose-300"
                                      title="Mute & exclude from future matchmaking"
                                    >
                                      Block
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        playtableApi.reportPlayer({
                                          targetPublicId: m.senderPublicId,
                                          tableId: view.tableId,
                                          reason: 'chat_conduct',
                                          details: m.text,
                                        })
                                      }
                                      className="hover:text-amber-300"
                                    >
                                      Report
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </div>

              {replyToId && (
                <div className="px-2 py-1 rounded bg-indigo-950/70 border border-indigo-500/40 text-[10px] flex justify-between">
                  <span>Replying to message...</span>
                  <button type="button" onClick={() => setReplyToId(null)} className="text-slate-400">
                    Cancel
                  </button>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!chatInput.trim()) return;
                  onDispatch({
                    type: 'SEND_CHAT',
                    text: chatInput,
                    channel: chatChannel,
                    whisperToSeatId: whisperSeatId || null,
                    replyToId,
                  });
                  setChatInput('');
                  setReplyToId(null);
                }}
                className="flex gap-1.5"
              >
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={whisperSeatId ? 'Whisper message...' : 'Message or [[Card Name]]...'}
                  className="flex-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold"
                >
                  Send
                </button>
              </form>
            </div>
          )}

          {activeTab === 'tracking' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 font-bold text-sm text-white">
                <BarChart3 className="w-4 h-4 text-emerald-400" />
                <span>Track games</span>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Connect Play Nice MTG, Mythic Track, and TopDeck.gg webhooks to log matches, sync commanders and decks, and review Deck Era Strength (`1.8K · T9 · B4 · 32%`) after each game.
              </p>

              {onOpenReportModal && (
                <button
                  type="button"
                  onClick={onOpenReportModal}
                  className="w-full py-2 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold"
                >
                  Open Two-Tap Match Reporter
                </button>
              )}

              {(
                [
                  ['play_nice', 'Play Nice MTG Webhook'],
                  ['mythic_track', 'Mythic Track Webhook'],
                  ['topdeck', 'TopDeck.gg Sync URL'],
                ] as const
              ).map(([prov, label]) => (
                <div key={prov} className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5">
                  <div className="font-semibold text-slate-200 text-[11px]">{label}</div>
                  <input
                    value={webhookUrls[prov]}
                    onChange={(e) => setWebhookUrls((prev) => ({ ...prev, [prov]: e.target.value }))}
                    placeholder="https://api.example.com/webhook/match"
                    className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-800 text-[10px]"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await playtableApi.saveTrackingWebhook({
                          provider: prov,
                          webhookUrl: webhookUrls[prov],
                          enabled: Boolean(webhookUrls[prov].trim()),
                        });
                        setWebhookStatus(`Saved ${label} integration!`);
                      } catch (err: any) {
                        setWebhookStatus(err.message);
                      }
                    }}
                    className="w-full py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[10px]"
                  >
                    Save {label.split(' ')[0]} Sync
                  </button>
                </div>
              ))}

              {webhookStatus && <div className="text-[10px] text-emerald-300">{webhookStatus}</div>}
            </div>
          )}

          {activeTab === 'stream' && (
            <div className="space-y-3">
              <StreamCompanionAndObsSuite
                view={view}
                overlaySecret={overlaySecret}
                onOverlaySecretChanged={onOverlaySecretChanged}
                onDispatch={onDispatch}
              />

              <a
                href={privateHandUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full py-2 px-3 rounded-xl bg-indigo-600/30 border border-indigo-400/60 text-indigo-100 font-bold flex items-center justify-center gap-1.5"
              >
                <span>Open Private Hand / OBS Dock Tab</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-3">
              <div className="font-bold text-sm text-white">View & Audio Settings</div>

              <div className="space-y-1">
                <div className="text-[10px] uppercase font-bold text-slate-400">View Mode (Desktop)</div>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => onChangeViewMode('follow')}
                    className={`py-1.5 rounded-lg font-bold ${
                      viewMode === 'follow' ? 'bg-indigo-600/30 border border-indigo-400 text-white' : 'bg-slate-900 text-slate-400'
                    }`}
                  >
                    Focus + Overview
                  </button>
                  <button
                    type="button"
                    onClick={() => onChangeViewMode('grid')}
                    className={`py-1.5 rounded-lg font-bold ${
                      viewMode === 'grid' ? 'bg-indigo-600/30 border border-indigo-400 text-white' : 'bg-slate-900 text-slate-400'
                    }`}
                  >
                    Grid View
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-[10px] uppercase font-bold text-slate-400">Sidebar Position</div>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => onChangeSide('left')}
                    className={`py-1.5 rounded-lg font-bold ${
                      side === 'left' ? 'bg-indigo-600/30 border border-indigo-400 text-white' : 'bg-slate-900 text-slate-400'
                    }`}
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    onClick={() => onChangeSide('right')}
                    className={`py-1.5 rounded-lg font-bold ${
                      side === 'right' ? 'bg-indigo-600/30 border border-indigo-400 text-white' : 'bg-slate-900 text-slate-400'
                    }`}
                  >
                    Right
                  </button>
                </div>
              </div>

              <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer">
                <span>Reduced Motion</span>
                <input
                  type="checkbox"
                  checked={reducedMotion}
                  onChange={(e) => onToggleReducedMotion(e.target.checked)}
                />
              </label>

              <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold">Synthesized Audio</span>
                  <button
                    type="button"
                    onClick={() => setSoundPrefs(soundEngine.updatePrefs({ muted: !soundPrefs.muted }))}
                    className="px-2 py-0.5 rounded bg-slate-800 text-[10px] flex items-center gap-1"
                  >
                    {soundPrefs.muted ? <VolumeX className="w-3 h-3 text-rose-400" /> : <Volume2 className="w-3 h-3 text-emerald-400" />}
                    <span>{soundPrefs.muted ? 'Muted (M)' : 'Active (M)'}</span>
                  </button>
                </div>
                {(['drawSlide', 'playFlick', 'tableThud', 'shuffleRiffle', 'hoverTick'] as SoundEventId[]).map((ev) => (
                  <div key={ev} className="flex items-center gap-2 text-[10px]">
                    <span className="w-20 text-slate-400">{ev}</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={soundPrefs.volumes[ev]}
                      onChange={(e) =>
                        setSoundPrefs(
                          soundEngine.updatePrefs({ eventVolumes: { [ev]: Number.parseFloat(e.target.value) } })
                        )
                      }
                      className="flex-1 accent-amber-500"
                    />
                  </div>
                ))}
              </div>

              {/* Keyboard Shortcuts Quick Reference */}
              <div className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1 text-[10px] text-slate-400">
                <div className="font-bold text-slate-200 flex items-center gap-1 mb-1">
                  <Keyboard className="w-3.5 h-3.5" />
                  <span>Keyboard Shortcuts</span>
                </div>
                <div className="flex justify-between"><span>Draw Card</span><kbd className="text-amber-300">D</kbd></div>
                <div className="flex justify-between"><span>Untap All</span><kbd className="text-amber-300">U</kbd></div>
                <div className="flex justify-between"><span>Shuffle Library</span><kbd className="text-amber-300">R</kbd></div>
                <div className="flex justify-between"><span>Next Phase</span><kbd className="text-amber-300">Space</kbd></div>
                <div className="flex justify-between"><span>Life +1 / −1</span><kbd className="text-amber-300">↑ / ↓</kbd></div>
                <div className="flex justify-between"><span>Wait / Clear Flag</span><kbd className="text-amber-300">W</kbd></div>
                <div className="flex justify-between"><span>Undo Last Action</span><kbd className="text-amber-300">Ctrl+Z</kbd></div>
              </div>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
